import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { Component, Input, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { ProductKeys } from '@config/keys/product.keys';
import { ShopKeys } from '@config/keys/shop.keys';

import { DoodProductModel } from '@core/models/product.model';
import { IZerosixAsset } from '@core/models/zerosix.model';

import { CartService } from '@core/services/cart/cart.service';

import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { Store } from '@ngrx/store';
import { ProductAddModalConfig } from '@shared/modals/product-add-modal/product-add-modal.component';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';
import { CART_ACTIONS } from '@store/cart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';

export type ZerosixOfferedProductPickerModalConfig = ModalConfig<{
  handle: 'zerosix-offered-product-picker-modal';
  inputs: {
    asset?: IZerosixAsset;
  };
}>;

@Component({
  selector: 'app-zerosix-offered-product-picker-modal',
  templateUrl: './zerosix-offered-product-picker-modal.component.html',
  standalone: false,
})
export class ZerosixOfferedProductPickerModalComponent
  extends ModalScrollBlockBase
  implements OnDestroy, GetModalInterface<ZerosixOfferedProductPickerModalConfig>
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'zerosix-offered-product-picker-modal' as const;

  @Input() backgroundColor?: IContentBuilderFieldColor;

  @Input() asset: IZerosixAsset | undefined;

  index$: Observable<number> = this.modalsService
    .getData(ZerosixOfferedProductPickerModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 6)));

  modalData$ = this.modalsService.getData(ZerosixOfferedProductPickerModalComponent.handle);

  protected readonly shopIdFromCart$: Observable<undefined | string> =
    this.cartSelector.selectActive$.pipe(map(cart => cart?.shop));

  products$: Observable<DoodProductModel[]> = combineLatest({
    shop: this.shopIdFromCart$.pipe(
      switchMap(shopId => (shopId ? this.shopSelector.selectShop(shopId) : of(undefined))),
    ),
    modalData: this.modalData$,
  }).pipe(
    map(({ shop, modalData }) => {
      if (!shop) {
        return [];
      }

      return shop[ShopKeys.Products].filter(p => {
        const externalId = p[ProductKeys.ExternalId];
        const plu = p[ProductKeys.Plu];
        if (externalId || plu) {
          return (
            (externalId && modalData?.data?.asset?.external_ids?.includes(externalId)) ||
            (plu && modalData?.data?.asset?.external_ids?.includes(plu))
          );
        }
        return false;
      });
    }),
  );

  constructor(
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private readonly cartService: CartService,
    private readonly modalsService: ModalsService<
      [ZerosixOfferedProductPickerModalConfig, ProductAddModalConfig]
    >,
    private readonly zerosixService: ZerosixService,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();
    this.actions$
      .pipe(ofType(CART_ACTIONS.updateProductInCart))
      .pipe(takeUntilDestroyed())
      .subscribe(({ action }) => {
        if (action === 'add' || action === 'increase') {
          this.close();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close(ZerosixOfferedProductPickerModalComponent.handle);
  }

  onNavigateTo(product: DoodProductModel, shopId: string | null | undefined): void {
    if (!shopId) {
      return;
    }
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: {
          channel: 'loyalty',
          reference: 'zerosix',
        },
      }),
    );
  }

  addVoucherWithoutProduct(): void {
    this.close();
    this.modalsService.emitEvent({
      data: { type: 'productSelected' },
      handle: ZerosixOfferedProductPickerModalComponent.handle,
    });
  }

  cancel(): void {
    this.modalData$.pipe(take(1), takeUntil(this._destroyerRef)).subscribe(modalData => {
      const asset = modalData?.data?.asset;
      if (!asset) {
        this.close();
        return;
      }
      const coupon = this.zerosixService.calculateCouponFromAsset(asset);
      if (!coupon) {
        return;
      }
      this.cartService.removeCoupon(coupon);
      this.close();
    });
  }
}
